/**
 * Runtime voxel destruction (клиент) — визуализация разрушения bodyVox-сетки.
 *
 * Референсы (паттерны, не копипаст):
 * - [Milan Bonten — Voxel Physics / Kudzu](https://milanbonten.github.io/voxel-physics-engine):
 *   разрушение через связность: flood fill по кадрам, отделение компонент, пересчёт тела.
 *   У нас на сервере: `DetachSmallestAliveComponents` (6-связная BFS) в
 *   `server/internal/game/bodyvox.go` — «оторванные» куски выкидываются из битмаски.
 * - [Unity — Voxel Destruction Engine (Kajos, обсуждение)](https://discussions.unity.com/t/voxel-destruction-engine/520135):
 *   вариация на raycast по существующему мешу; у нас во `mesh.frag` — короткий **ray-march
 *   вдоль -N** в body-local + union сфер + 6-соседняя битмаска.
 *
 * Лимит K сфер в шейдере: при большом числе мёртвых ячеек берём K ближайших к **камере**
 * центров (читаемые дыры на экране), а не первые K в обходе битмаски.
 */

import {
  BODY_VOX_CUBE,
  BODY_VOX_GRID_BYTES,
  BODY_VOX_OX,
  BODY_VOX_OY,
  BODY_VOX_OZ,
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
} from "./bodyVox";
import type { BodyVoxSkinBake } from "./bodyVoxProxyBake";
import {
  rigidBodyLocalToWorld,
  worldPosForBodyVoxProxyCell,
} from "./bodyVoxProxyBake";

/** Должно совпадать с `BODY_DEAD_VOL_MAX` в `mesh.frag`. */
export const BODY_DEAD_VOL_SHADER_MAX = 128;

const CELL_COUNT = BODY_VOX_SX * BODY_VOX_SY * BODY_VOX_SZ;

/** Половина диагонали ячейки × перекрытие; часть «зазора» дублируется в `DEAD_VOL_SKIN_SLACK` (mesh.frag). */
export function bodyDeadVolSphereRadius(): number {
  const halfDiag = (BODY_VOX_CUBE * Math.sqrt(3)) * 0.5;
  return halfDiag * 2.85 + BODY_VOX_CUBE * 1.2;
}

type InnerBakeRef = { bodyVoxProxyBakes?: Map<number, BodyVoxSkinBake> };

const _posScratch = new Float32Array(CELL_COUNT * 3);
const _distScratch = new Float32Array(CELL_COUNT);
const _pickedScratch = new Uint8Array(CELL_COUNT);

function cellAliveInBitmap(b: Uint8Array, cellIdx: number): boolean {
  return (b[cellIdx >> 3]! & (1 << (cellIdx & 7))) !== 0;
}

/**
 * Заполняет `out` как vec4-пачку: xyz — центр мёртвой ячейки в мире (скин + uModel), w — радиус.
 *
 * @param shellVox — v13: если задан, ячейка считается «дырой» при мёртвом core **или** мёртвом shell
 *   (иначе дыры только в оболочке не получали world-сфер и рвались на idle по битмапе).
 * @param cameraWorld — позиция камеры; если задана, выбираются ближайшие к камере мёртвые ячейки
 *   до лимита K (runtime editor / Teardown-подобный приоритет «видимого» разрушения).
 */
export function fillDeadBodyVoxWorldSpheres(
  vox: Uint8Array,
  inst: {
    x: number;
    y: number;
    z: number;
    yaw: number;
    yawOffset: number;
    innerBoneMatrices: Float32Array;
    innerMesh: InnerBakeRef | null;
  },
  im: Float32Array,
  skinScratch: Float32Array,
  wp: Float32Array,
  out: Float32Array,
  cameraWorld?: { x: number; y: number; z: number },
  shellVox?: Uint8Array | null,
): number {
  const bones = inst.innerBoneMatrices;
  if (!bones.length) return 0;
  const bake = inst.innerMesh?.bodyVoxProxyBakes?.get(inst.yawOffset);
  const r = bodyDeadVolSphereRadius();
  const sxsy = BODY_VOX_SX * BODY_VOX_SY;
  const shellOk =
    shellVox !== null &&
    shellVox !== undefined &&
    shellVox.length === BODY_VOX_GRID_BYTES;

  let nDead = 0;
  for (let cellIdx = 0; cellIdx < CELL_COUNT; cellIdx++) {
    const coreDead = !cellAliveInBitmap(vox, cellIdx);
    const shellDead = shellOk && !cellAliveInBitmap(shellVox!, cellIdx);
    if (!coreDead && !shellDead) continue;

    const iz = (cellIdx / sxsy) | 0;
    const rem = cellIdx - iz * sxsy;
    const iy = (rem / BODY_VOX_SX) | 0;
    const ix = rem % BODY_VOX_SX;
    const lx = BODY_VOX_OX + (ix + 0.5) * BODY_VOX_CUBE;
    const ly = BODY_VOX_OY + (iy + 0.5) * BODY_VOX_CUBE;
    const lz = BODY_VOX_OZ + (iz + 0.5) * BODY_VOX_CUBE;

    let wx: number;
    let wy: number;
    let wz: number;
    if (
      bake &&
      worldPosForBodyVoxProxyCell(bake, cellIdx, bones, im, skinScratch, wp)
    ) {
      wx = wp[0]!;
      wy = wp[1]!;
      wz = wp[2]!;
    } else {
      rigidBodyLocalToWorld(inst.x, inst.y, inst.z, inst.yaw, lx, ly, lz, wp);
      wx = wp[0]!;
      wy = wp[1]!;
      wz = wp[2]!;
    }

    const pBase = nDead * 3;
    _posScratch[pBase] = wx;
    _posScratch[pBase + 1] = wy;
    _posScratch[pBase + 2] = wz;

    if (cameraWorld) {
      const dx = wx - cameraWorld.x;
      const dy = wy - cameraWorld.y;
      const dz = wz - cameraWorld.z;
      _distScratch[nDead] = dx * dx + dy * dy + dz * dz;
    }
    nDead++;
  }

  if (nDead === 0) return 0;

  const K = Math.min(BODY_DEAD_VOL_SHADER_MAX, nDead);
  let outN = 0;

  if (cameraWorld && nDead > K) {
    _pickedScratch.fill(0, 0, nDead);
    for (let k = 0; k < K; k++) {
      let bestI = -1;
      let bestD = Infinity;
      for (let i = 0; i < nDead; i++) {
        if (_pickedScratch[i]) continue;
        const d = _distScratch[i]!;
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      }
      if (bestI < 0) break;
      _pickedScratch[bestI] = 1;
      const o = outN * 4;
      const pb = bestI * 3;
      out[o] = _posScratch[pb]!;
      out[o + 1] = _posScratch[pb + 1]!;
      out[o + 2] = _posScratch[pb + 2]!;
      out[o + 3] = r;
      outN++;
    }
    return outN;
  }

  for (let i = 0; i < K; i++) {
    const o = i * 4;
    const pb = i * 3;
    out[o] = _posScratch[pb]!;
    out[o + 1] = _posScratch[pb + 1]!;
    out[o + 2] = _posScratch[pb + 2]!;
    out[o + 3] = r;
  }
  return K;
}
